import { FC, ReactNode } from 'react';
import { Box, Tab, Tabs, ToggleButton, ToggleButtonGroup } from '@mui/material';

/** A single focused child section within a category tab, e.g. "Catalogues" within
 * the "Catalogue & Ordering" category. `content` is optional: omit it when the consumer's
 * existing content area is already driven by its own state (e.g. a legacy tabValue-keyed
 * Grid) and this component is only supplying the navigation chrome, not the content itself. */
export interface FormTabsWithSideNavChild {
  key: string;
  label: string;
  content?: ReactNode;
}

/** A top-level category tab, grouping one or more focused child sections. */
export interface FormTabsWithSideNavCategory {
  key: string;
  label: string;
  children: FormTabsWithSideNavChild[];
}

interface FormTabsWithSideNavProps {
  categories: FormTabsWithSideNavCategory[];
  activeCategory: string;
  activeChild: string;
  onCategoryChange: (categoryKey: string) => void;
  onChildChange: (childKey: string) => void;
}

/**
 * Shared layout primitive: a horizontal category Tabs bar. When the active category has more
 * than one focused child section, those children are presented as a ToggleButtonGroup within
 * the tab's own content area (rather than a separate side nav column). Categories with only
 * one child render that child's content directly (no toggle group needed).
 *
 * Intended to replace overly-flat single-level Tabs bars on large forms (Procurement
 * Products, RecipeV2 Detail, Supplier Form) without inventing a bespoke layout per page.
 */
const FormTabsWithSideNav: FC<FormTabsWithSideNavProps> = ({
  categories,
  activeCategory,
  activeChild,
  onCategoryChange,
  onChildChange,
}) => {
  const category = categories.find((c) => c.key === activeCategory) ?? categories[0];
  const child = category?.children.find((c) => c.key === activeChild) ?? category?.children[0];

  return (
    <Box>
      <Tabs
        value={category?.key ?? false}
        onChange={(_, v) => onCategoryChange(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {categories.map((c) => (
          <Tab key={c.key}
            label={c.label}
            value={c.key} />
        ))}
      </Tabs>

      <Box sx={{ mt: 3 }}>
        {category && category.children.length > 1 && (
          <ToggleButtonGroup
            value={child?.key ?? false}
            exclusive
            onChange={(_, v) => v && onChildChange(v)}
            size="small"
            sx={{ mb: 3 }}
          >
            {category.children.map((c) => (
              <ToggleButton
                key={c.key}
                value={c.key}
              >
                {c.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        <Box sx={{ minWidth: 0 }}>
          {child?.content}
        </Box>
      </Box>
    </Box>
  );
};

export default FormTabsWithSideNav;
